import { NgTemplateOutlet } from "@angular/common";
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  inject,
  LOCALE_ID,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Meta } from "@angular/platform-browser";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { PrimeNGConfig } from "primeng/api";
import type { APTContext } from "src/app/services/aptContext/apt-context";
import {
  DEFAULT_MENU_ZINDEX,
  DEFAULT_MODAL_ZINDEX,
  DEFAULT_OVERLAY_ZINDEX,
  DEFAULT_TOOLTIP_ZINDEX,
  DEFAULT_WINDOW_ZINDEX,
  siteDescription,
} from "src/config";
import { LoggedInLayoutComponent } from "src/standard/layouts/logged-in-layout/logged-in-layout.component";
import type { TelemetryService } from "src/standard/logging/telemetry.service";
import { HttpCancelService } from "src/standard/services/http-cancel.service";
import { lazyInject } from "src/standard/services/lazy-inject.service";
import { StandalonePageService } from "src/standard/services/standalone-page.service";
import { initializeLanguageProperties } from "src/standard/ts/initializeLanguageProperties";
import { userSummary$ } from "../security/security.service";
import NProgress from "../ts/nprogress-customizations";
import type { StandardFocusRingHelperService } from "src/standard/standard-focus-ring-helper/standard-focus-ring-helper.service";
import { isWorkspaceListRoute } from "src/standard/ts/routeUtils";
import { ENGAGEMENT_MEMBER_FIRM_ID_KEY } from "src/app/classic/classic-interceptor";
import { ScrollbarWidthService } from "src/standard/services/scrollbar-width.service";

export interface RouterData {
  title?: string;
  helpSiteId?: string;
}

@Component({
  standalone: true,
  selector: "app-root",
  templateUrl: "./app.component.html",
  imports: [NgTemplateOutlet, RouterOutlet, LoggedInLayoutComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private metaService = inject(Meta);
  ngLocale = inject(LOCALE_ID);

  private readonly router = inject(Router);
  private readonly httpCancelService = inject(HttpCancelService);
  private readonly primengConfig = inject(PrimeNGConfig);
  protected readonly standalonePageService = inject(StandalonePageService);
  protected readonly scrollbarWidthService = lazyInject<ScrollbarWidthService>(
    import("src/standard/services/scrollbar-width.service").then(
      (m) => m.ScrollbarWidthService,
    ),
  );

  // Lazy-inject these so they are not loaded until the app is initialized
  // but they're still available for use in the app
  // and are providedIn root so they stay alive for the lifetime of the app
  private readonly aptContext = lazyInject<APTContext>(
    import("src/app/services/aptContext/apt-context").then((m) => m.APTContext),
  );
  private readonly standardFocusRingHelperService =
    lazyInject<StandardFocusRingHelperService>(
      import("src/standard/standard-focus-ring-helper/standard-focus-ring-helper.service").then(
        (m) => m.StandardFocusRingHelperService,
      ),
    );
  private readonly telemetryService = lazyInject<TelemetryService>(
    import("src/standard/logging/telemetry.service").then(
      (m) => m.TelemetryService,
    ),
  );

  private syncCssVariables = afterRenderEffect(() => {
    const scrollbarWidthService = this.scrollbarWidthService();
    if (!scrollbarWidthService) {
      return;
    }

    const scrollbarWidth = scrollbarWidthService.scrollbarWidth();
    if (typeof scrollbarWidth !== "string") {
      return;
    }

    document.body.style.setProperty("--scrollbar-width", scrollbarWidth);
  });

  constructor() {
    initializeLanguageProperties(this.ngLocale, document.dir);

    this.primengConfig.zIndex = {
      window: DEFAULT_WINDOW_ZINDEX,
      modal: DEFAULT_MODAL_ZINDEX,
      menu: DEFAULT_MENU_ZINDEX,
      overlay: DEFAULT_OVERLAY_ZINDEX,
      tooltip: DEFAULT_TOOLTIP_ZINDEX,
    };

    // Update the meta site description tag
    this.metaService.updateTag({
      name: "description",
      content: siteDescription,
    });

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event): void => {
      if (event instanceof NavigationStart) {
        this.httpCancelService.cancelPendingRequests();

        NProgress.start();

        if (event.url === "/maintenance" || event.url === "/access-denied") {
          userSummary$.set([true, null]);
        }
      } else if (event instanceof NavigationEnd) {
        NProgress.done();

        const isOnWorkspaceList = isWorkspaceListRoute(event.url);
        if (isOnWorkspaceList) {
          this.clearEngagementStorage();
        }
      } else if (event instanceof NavigationCancel) {
        NProgress.done();
      } else if (event instanceof NavigationError) {
        NProgress.done();
      }
    });
  }

  clearEngagementStorage() {
    sessionStorage.removeItem(ENGAGEMENT_MEMBER_FIRM_ID_KEY);
  }

  protected readonly userSummary = userSummary$;
}
